import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, CircularProgress, Divider, Grid, IconButton, InputAdornment, MenuItem, Modal, Select, Stack, TextField, Typography } from '@mui/material';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { handlePreventKeyDownDecimal } from '../../../utility/helpers/commonFunction';
import { MuiSelectWithFocus, MuiTextFieldWithFocus } from '../../../config/MaterialUIConstants';

const FEE_CALCULATION_TYPES = {
    PERCENTAGE: 'percentage',
    FIXED_AMOUNT: 'fixed_amount',
};

const MODAL_STYLE = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    borderRadius: '12px',
    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.18)',
    overflow: 'hidden',
    maxWidth: 'calc(100vw - 28px)',
};

const LABEL_STYLE = {
    color: 'rgba(0, 0, 0, 0.87)',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: 'normal',
    mb: '8px',
};

/**
 * Returns adornment color: black when value is entered, grey when empty/zero
 */
const getAdornmentColor = (value) => {
    if (value === '' || value === undefined || value === null) {
        return '#939393';
    }

    const num = Number(value);

    if (isNaN(num) || num === 0) {
        return '#939393';
    }

    return 'rgba(0, 0, 0, 0.87)';
};

const FeesModal = ({
    type = 'invoice',
    loader = false,
    toolbarBg = 'white',
    color = 'rgba(0, 0, 0, 0.87)',
    showBoxShadow = false,
    width = '720px',
    open,
    onClose,
    onSave,
    updateIndex,
    setUpdateIndex,
    discountProgram = [],
}) => {
    const [nameError, setNameError] = useState('');

    const isEditMode = updateIndex !== -1;

    const {
        handleSubmit,
        setValue,
        control,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            name: '',
            calculationType: FEE_CALCULATION_TYPES.FIXED_AMOUNT,
            percentage: '',
            amount: '',
        },
    });

    const selectedCalculationType = useWatch({
        control,
        name: 'calculationType',
        defaultValue: FEE_CALCULATION_TYPES.FIXED_AMOUNT,
    });

    // Watch live values for adornment color
    const percentageValue = useWatch({ control, name: 'percentage', defaultValue: '' });
    const amountValue = useWatch({ control, name: 'amount', defaultValue: '' });

    const handleClose = useCallback(() => {
        if (!loader) {
            setUpdateIndex(-1);
            reset();
            onClose(false);
            setNameError('');
        }
    }, [loader, onClose, reset, setUpdateIndex]);

    useEffect(() => {
        if (isEditMode && discountProgram?.[updateIndex]) {
            const currentFee = discountProgram[updateIndex];

            const calculationType = currentFee?.calculationType || FEE_CALCULATION_TYPES.FIXED_AMOUNT;

            setValue('name', currentFee?.name || '');
            setValue('calculationType', calculationType);
            if (calculationType === FEE_CALCULATION_TYPES.PERCENTAGE) {
                setValue('percentage', currentFee?.percentage || '');
                setValue('amount', '');
            } else {
                setValue('amount', currentFee?.amount || '');
                setValue('percentage', '');
            }
        } else {
            reset();
        }
    }, [updateIndex, discountProgram, setValue, reset, isEditMode]);

    const handleFormSubmit = useCallback(
        (values) => {
            setNameError('');

            const nameExists = discountProgram.some(
                (fee, index) => fee.name.trim()?.toLowerCase() === values.name.trim()?.toLowerCase() && index !== updateIndex
            );

            if (nameExists) {
                setNameError('Fee name already exists. Please choose a different name.');
                return;
            }

            let payload = {
                name: values.name,
                type,
                calculationType: values.calculationType,
                isDefaultInvoice: !!discountProgram[updateIndex]?.isDefaultInvoice,
                isDefaultEstimate: !!discountProgram[updateIndex]?.isDefaultEstimate,
            };

            if (values.calculationType === FEE_CALCULATION_TYPES.PERCENTAGE) {
                payload.percentage = Number(values.percentage);
            } else {
                payload.amount = Number(values.amount);
            }

            onSave(isEditMode ? 'update' : 'add', payload);
        },
        [discountProgram, isEditMode, onSave, type, updateIndex]
    );

    const handlePercentageChange = useCallback((onChange, e) => {
        const value = e.target.value;
        if (value === '' || isNaN(value)) {
            onChange('');
        } else {
            const numericValue = Math.min(100, Math.max(0, Number(value)));
            onChange(numericValue);
        }
    }, []);

    const handleAmountChange = useCallback((onChange, event) => {
        const inputValue = event.target.value;
        const numericValue = inputValue.replace(/[^\d.]/g, '');
        if (numericValue === '' || /^\d*(\.\d{0,2})?$/.test(numericValue)) {
            onChange(numericValue);
        }
    }, []);

    return (
        <Modal
            open={open}
            onClose={(event, reason) => {
                if (reason && reason === 'backdropClick') {
                    return;
                }
                handleClose();
            }}
            aria-labelledby="fees-modal-title"
            aria-describedby="fees-modal-description"
        >
            <Box sx={{ ...MODAL_STYLE, width: width, height: 'fit-content' }}>
                {/* Header */}
                <Box
                    sx={{
                        background: '#FFF',
                        boxShadow: 'none',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.10)!important',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        minHeight: '62px',
                        px: '24px',
                    }}
                >
                    <Typography id="fees-modal-title" variant="h6" color="#1F1F1F" fontSize="20px" fontWeight={500} lineHeight="32px">
                        {isEditMode ? 'Edit Fees' : 'Add Fees'}
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        aria-label="Close modal"
                        disabled={loader}
                        tabIndex={5}
                        sx={{ color: '#1F1F1F', width: 36, height: 36, mr: '-8px' }}
                    >
                        <CloseIcon sx={{ fontSize: 32 }} />
                    </IconButton>
                </Box>

                <form onSubmit={handleSubmit(handleFormSubmit)}>
                    <Box sx={{ px: '24px', pt: '24px', pb: '22px' }}>
                        <Grid container columnSpacing={3} rowSpacing={3}>
                            {/* Fee Name */}
                            <Grid item xs={12}>
                                <Typography variant="body1" sx={LABEL_STYLE}>
                                    Fee Name
                                </Typography>
                                <Controller
                                    name="name"
                                    control={control}
                                    rules={{
                                        required: 'Please enter fee name',
                                        minLength: { value: 2, message: 'Name must be at least 2 characters' },
                                        maxLength: { value: 50, message: 'Name must not exceed 50 characters' },
                                    }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            style={{ marginTop: '3px', padding: '0' }}
                                            id="fee-name"
                                            variant="outlined"
                                            fullWidth
                                            error={!!errors.name || !!nameError}
                                            helperText={errors.name ? errors.name.message : nameError}
                                            InputLabelProps={{ shrink: field.value?.length > 0 }}
                                            sx={MuiTextFieldWithFocus}
                                            inputProps={{ tabIndex: 1 }}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Calculation Type */}
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body1" sx={LABEL_STYLE}>
                                    Type
                                </Typography>
                                <Controller
                                    name="calculationType"
                                    control={control}
                                    rules={{ required: 'Please select fee type' }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            fullWidth
                                            displayEmpty
                                            id="fee-calculation-type"
                                            variant="outlined"
                                            MenuProps={{ style: { maxHeight: '400px' } }}
                                            sx={{
                                                ...MuiSelectWithFocus,
                                                '& .MuiSelect-select': {
                                                    fontWeight: 400,
                                                    fontSize: '16px',
                                                },
                                            }}
                                            // sx={MuiSelectWithFocus}
                                            inputProps={{ tabIndex: 2 }}
                                            onChange={(event) => {
                                                field.onChange(event.target.value);
                                                setValue('percentage', '');
                                                setValue('amount', '');
                                            }}
                                        >
                                            <MenuItem value={FEE_CALCULATION_TYPES.PERCENTAGE}>Percent</MenuItem>
                                            <MenuItem value={FEE_CALCULATION_TYPES.FIXED_AMOUNT}>Cost</MenuItem>
                                        </Select>
                                    )}
                                />
                            </Grid>

                            {/* Percentage Value */}
                            {selectedCalculationType === FEE_CALCULATION_TYPES.PERCENTAGE && (
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body1" sx={LABEL_STYLE}>
                                        Value
                                    </Typography>
                                    <Controller
                                        name="percentage"
                                        control={control}
                                        rules={{
                                            required: 'Please enter fee percentage',
                                            min: { value: 0, message: 'Percentage cannot be less than 0%' },
                                            max: { value: 100, message: 'Percentage cannot be more than 100%' },
                                        }}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                style={{ marginTop: '3px' }}
                                                id="fee-percentage"
                                                variant="outlined"
                                                fullWidth
                                                type="number"
                                                error={!!errors.percentage}
                                                helperText={errors.percentage ? errors.percentage.message : ''}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <Typography
                                                                sx={{
                                                                    color: getAdornmentColor(percentageValue),
                                                                    fontSize: '16px',
                                                                    fontWeight: 400,
                                                                    transition: 'color 0.15s',
                                                                }}
                                                            >
                                                                %
                                                            </Typography>
                                                        </InputAdornment>
                                                    ),
                                                    inputProps: {
                                                        min: 0,
                                                        max: 100,
                                                        step: 0.01,
                                                        tabIndex: 3,
                                                    },
                                                }}
                                                onKeyDown={handlePreventKeyDownDecimal}
                                                onChange={(e) => handlePercentageChange(field.onChange, e)}
                                                sx={MuiTextFieldWithFocus}
                                            />
                                        )}
                                    />
                                </Grid>
                            )}

                            {/* Fixed Amount Value */}
                            {selectedCalculationType === FEE_CALCULATION_TYPES.FIXED_AMOUNT && (
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body1" sx={LABEL_STYLE}>
                                        Value
                                    </Typography>
                                    <Controller
                                        name="amount"
                                        control={control}
                                        rules={{
                                            required: 'Please enter fee amount',
                                            min: { value: 0, message: 'Fee amount cannot be less than 0' },
                                        }}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                style={{ marginTop: '3px' }}
                                                id="fee-amount"
                                                variant="outlined"
                                                fullWidth
                                                type="number"
                                                error={!!errors.amount}
                                                helperText={errors.amount ? errors.amount.message : ''}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Typography
                                                                sx={{
                                                                    color: getAdornmentColor(amountValue),
                                                                    fontSize: '16px',
                                                                    fontWeight: 400,
                                                                    transition: 'color 0.15s',
                                                                }}
                                                            >
                                                                $
                                                            </Typography>
                                                        </InputAdornment>
                                                    ),
                                                    inputProps: {
                                                        min: 0,
                                                        step: 0.01,
                                                        tabIndex: 3,
                                                    },
                                                }}
                                                onChange={(event) => handleAmountChange(field.onChange, event)}
                                                sx={MuiTextFieldWithFocus}
                                            />
                                        )}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Box>

                    <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.12)' }} />

                    {/* Footer */}
                    <Box
                        sx={{
                            borderTop: 'none',
                            px: '16px',
                            py: '16px',
                            display: 'flex',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Stack spacing={2} direction="row">
                            <Button variant="outlined" color="primary" disabled={loader} onClick={handleClose} tabIndex={4} sx={{ minWidth: 103, height: 42 }}>
                                CANCEL
                            </Button>
                            <Button variant="contained" color="primary" type="submit" disabled={loader} tabIndex={3} sx={{ minWidth: 82, height: 42 }}>
                                {loader ? <CircularProgress size={24} sx={{ color: 'white' }} /> : isEditMode ? 'UPDATE' : 'SAVE'}
                            </Button>
                        </Stack>
                    </Box>
                </form>
            </Box>
        </Modal>
    );
};

export default memo(FeesModal);
